import { formatCNPJ } from '@format';
import TextField from '@material-ui/core/TextField';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { excluirAlert } from 'app/services/alerts';
import pessoaApi from 'app/services/api/pessoaApi';
import { removeCpfCnpjMask } from 'app/utils/mask';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

const filter = createFilterOptions();

const confirmacaoEdicao = (confirmarFunc, cancelarFunc, papel, fetchPapel) => {
  excluirAlert({
    opcoes: {
      title: 'O CNPJ informado já está cadastrado. Deseja atualizá-lo?',
      text: '',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    },
    confirmar: async () => confirmarFunc(papel, fetchPapel),
    cancelar: async () => cancelarFunc(),
  });
};

const handleChange = (
  onChange,
  setInputValue,
  setShowAlert,
  setOptions,
  onSelect,
  papel,
  fetchPapel,
  permiteEditar
) => {
  return (event, newValue, reason) => {
    if (typeof newValue === 'string') {
      onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: removeCpfCnpjMask(newValue) } });
      setInputValue(newValue);
    } else if (newValue && newValue.inputValue) {
      onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: removeCpfCnpjMask(newValue.inputValue) } });
      setInputValue(newValue.inputValue);
    } else if (reason === 'clear') {
      if (permiteEditar) {
        onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: undefined } });
      } else {
        onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: undefined } });
        onChange({ target: { id: 'pessoa.id', value: undefined } });
        onChange({ target: { id: 'pessoa.pessoaJuridica.id', value: undefined } });
      }
    } else {
      criarAlertaEdicaoPessoa(setShowAlert, setOptions, newValue, onChange, onSelect, setInputValue, papel, fetchPapel);
    }
  };
};

const handleInputChange = (onChange, setInputValue, setOptions, fetchData, options) => {
  return (event, value) => {
    const cnpjLimpo = removeCpfCnpjMask(value);

    if (!cnpjLimpo || cnpjLimpo.length <= 14) {
      onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: cnpjLimpo } });
      /**
       * Esse onChange abaixo é o responsavel por setar o dirty do formik como true, pois
       * está criando um atributo de pessoa que não existia no formulario
       *
       * onChange({ target: { id: 'pessoa.cpfCnpj', value: numberCnpj } });
       */
      setInputValue(formatCNPJ(cnpjLimpo));

      if (cnpjLimpo.length >= 3 && !options.find((pessoa) => pessoa?.cpfCnpj === cnpjLimpo)) {
        (async () => {
          const response = await fetchData(cnpjLimpo);

          setOptions(response.data);
        })();
      }
    }
  };
};

const handleBlur = (
  onBlur,
  options,
  setShowAlert,
  setOptions,
  onChange,
  setInputValue,
  onSelect,
  papel,
  fetchPapel
) => {
  return (event) => {
    const cnpjLimpo = removeCpfCnpjMask(event.target.value);
    const newValue = options.find((pessoa) => pessoa?.cpfCnpj === cnpjLimpo);

    if (cnpjLimpo.length === 14 && newValue) {
      criarAlertaEdicaoPessoa(setShowAlert, setOptions, newValue, onChange, onSelect, setInputValue, papel, fetchPapel);
    }

    if (onBlur) {
      onBlur(event);
    }
  };
};

const handleOptionLabel = (option) => {
  // Value selected with enter, right from the input
  if (typeof option === 'string') {
    return formatCNPJ(option) || '';
  }
  // Add "xxx" option created dynamically
  if (option.inputValue) {
    return formatCNPJ(option.inputValue) || '';
  }

  // Regular option
  return formatCNPJ(option?.cpfCnpj) || formatCNPJ(option?.pessoaJuridica?.cnpj);
};

const fetchData = pessoaApi.buscarPessoaJuridica;

function criarAlertaEdicaoPessoa(
  setShowAlert,
  setOptions,
  newValue,
  onChange,
  onSelect,
  setInputValue,
  papel,
  fetchPapel
) {
  setShowAlert(true);
  const confirmar = async (papel, fetchPapel) => {
    if (newValue.papeis?.includes(papel?.toUpperCase())) {
      fetchPapel(newValue[`${papel}Id`]);
      setShowAlert(false);
      return;
    }

    const res = await pessoaApi.buscarPessoaCompleta(newValue.id);

    const {
      data: { pessoa },
    } = res;
    setOptions([]);

    pessoa.cpfCnpj = pessoa?.pessoaJuridica?.cnpj;
    if (pessoa?.pessoaJuridica?.dataFundacao) {
      pessoa.pessoaJuridica.dataFundacao = moment(pessoa.pessoaJuridica.dataFundacao);
    }

    setShowAlert(false);
    onChange({ target: { id: 'id', value: res.data[`${papel}Id`] } });
    onChange({ target: { id: 'pessoa', value: { ...pessoa, tipoPessoa: 'JURIDICA' } } });
    onSelect();
  };
  const cancelar = () => {
    setOptions([]);
    setShowAlert(false);
    setInputValue('');
    onChange({ target: { id: 'pessoa.pessoaJuridica.cnpj', value: undefined } });
  };

  confirmacaoEdicao(confirmar, cancelar, papel, fetchPapel);
}

function PessoaJuridicaAutocomplete({
  onChange,
  value,
  onSelect = () => {},
  error,
  touched,
  onBlur,
  disabled,
  papel,
  fetchPapel,
  permiteEditar,
}) {
  // const [value, setValue] = useState(null);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    if (!showAlert) {
      if (inputValue && options && options.find((p) => p?.pessoaJuridica?.cnpj === inputValue)) {
        const pessoa = options.find((p) => p?.pessoaJuridica?.cnpj === inputValue);
        if (pessoa?.pessoaJuridica?.dataFundacao) {
          pessoa.pessoaJuridica.dataFundacao = moment(pessoa.pessoaJuridica.dataFundacao);
        }
        onChange({ target: { id: 'pessoa', value: pessoa } });
      }
    }
    // Memoização intencional: as funções são recriadas a cada render e incluí-las anularia o memo;
    // o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  return (
    <Autocomplete
      selectOnFocus
      freeSolo
      value={value}
      disabled={disabled}
      id="pessoa.pessoaJuridica.cnpj"
      options={options}
      inputValue={inputValue}
      disableClearable={value?.id === undefined}
      onChange={handleChange(
        onChange,
        setInputValue,
        setShowAlert,
        setOptions,
        onSelect,
        papel,
        fetchPapel,
        permiteEditar
      )}
      onInputChange={handleInputChange(onChange, setInputValue, setOptions, fetchData, options)}
      filterOptions={filter}
      getOptionLabel={handleOptionLabel}
      renderOption={(option) => formatCNPJ(option?.cpfCnpj) || formatCNPJ(option?.pessoaJuridica?.cnpj)}
      getOptionSelected={(option, valor) => {
        return option.id === valor.id;
      }}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label="CNPJ"
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={touched && error}
          helperText={touched && error}
          onBlur={handleBlur(
            onBlur,
            options,
            setShowAlert,
            setOptions,
            onChange,
            setInputValue,
            onSelect,
            papel,
            fetchPapel
          )}
        />
      )}
    />
  );
}

export default PessoaJuridicaAutocomplete;
